﻿namespace eXpress.services {

	export interface IDownloadStatusResult {
		readonly fileId: string;
		readonly status: number;
		readonly info: string;
		readonly originalFileName: string;
	}

	export interface IPartnerPhotoData {
		/** Mediastore file Id*/
		readonly Id?: string;
		/** Partner ref string*/
		readonly Code: string;
		readonly ImageFilename: string;
		readonly StoreType: services.StoreType;
		readonly ImageUrl: string;
	}

	export class Download {

		private downloadService: DownloadService;
		private downloader: core.Downloader;
		private downloadQueue: core.IDownloadQueueItem[];

		constructor(downloadService: DownloadService, downloader: core.Downloader) {
			this.downloadService = downloadService;
			this.downloader = downloader;
			this.downloadQueue = [];
		}

		public addToQueueAsync(photos: core.partners.PartnerPhoto[], flow: string): JQueryPromise<core.IDownloadQueueItem[]> {
			return this.createSessionAsync(photos, flow).then((sessionCreated: boolean) => {
				if (!sessionCreated) {
					return $.Deferred<core.IDownloadItem[]>().reject(new Error('Download session could not be created on server.')).promise();
				}

				return this.waitForAllDownloadsAsync();
			});
		}

		private createSessionAsync(photos: core.partners.PartnerPhoto[], flow: string): JQueryPromise<boolean> {
			const downloadQueueItems: core.IDownloadQueueItem[] = [];
			const partnerPhotoDatas: services.IPartnerPhotoData[] =[];
			photos.forEach(photo => {
				const partnerPhotoData: services.IPartnerPhotoData = {
					Id: eXpress.base.generateNewGuid(),
					Code: photo.code,
					ImageFilename: photo.name,
					ImageUrl: photo.downloadUrl,
					StoreType: photo.storeType
				};

				const downloadQueueItem: core.IDownloadQueueItem = {
					state: core.DownloadItemStates.WaitingForDownload,
					errorMessage: undefined,
					itemToDownload: photo,
					downloadedItem: {
						photoId: partnerPhotoData.Code, // is partner ref string
						name: partnerPhotoData.ImageFilename,
						mediastoreFileId: utils.required(partnerPhotoData.Id, "no media store file id for partner photo")
					},
				};

				downloadQueueItems.push(downloadQueueItem);
				partnerPhotoDatas.push(partnerPhotoData);
			});

			// Create Session
			return this.downloadService.createDownloadSessionForPartnerPhotosAsync(partnerPhotoDatas, core.context.pointOfSaleId, GetCookie("accid"), core.context.languageCode, flow)
				.then(sessionCreated => {
					if (sessionCreated)
						downloadQueueItems.forEach(dqi => this.downloadQueue.push(dqi));

					return sessionCreated;
				});
		}

		/** Check if already downloaded, imported or failted */
		private checkDownloadStatusAsync(): JQueryPromise<core.IDownloadQueueItem[] | null> {
			const fileIdsToCheck = this.downloadQueue
				.filter(i => i.state !== core.DownloadItemStates.DownloadError
					&& i.state !== core.DownloadItemStates.Imported
					&& i.state !== core.DownloadItemStates.ImportError
					&& i.state !== core.DownloadItemStates.NotFound)
				.filter(i => i.downloadedItem)
				.map(item => item.downloadedItem!.mediastoreFileId);

			return this.downloadService.downloadPartnerCheckStatusFilesAsync(fileIdsToCheck, GetCookie("accid"))
				.then((result: IDownloadStatusResult[]) => {
					const itemsChanged: core.IDownloadQueueItem[] = [];

					result.forEach((resultItem: IDownloadStatusResult) => {
						const queueItems = this.downloadQueue.filter(qi => qi.downloadedItem?.mediastoreFileId === resultItem.fileId);
						if (utils.isNonEmptyArray(queueItems)) {
							const queueItem = queueItems[0];
							const newState = resultItem.status;
							if (newState !== queueItem.state) {
								queueItem.state = newState;
								itemsChanged.push(queueItem);
								if (resultItem.info) {
									queueItem.errorMessage = "Photo '" + queueItem.itemToDownload.name + "' (id:" + (queueItem.downloadedItem?.mediastoreFileId || 'NA') + ") could not be not be downloaded or imported at SmartPhoto. ErrorMessage: " + resultItem.info;
								}
							}
						}
					});

					if (itemsChanged.length) {
						this.downloader.handleDownloadItem(this.downloadQueue.length, result.length);
					}

					if (this.hasUncompletedDownloads())
						return null;
					else
						return this.downloadQueue;
				});
		}

		private hasUncompletedDownloads(): boolean {
			return this.downloadQueue.some((i: core.IDownloadQueueItem) =>
				(i.state === core.DownloadItemStates.WaitingForDownload || i.state === core.DownloadItemStates.Downloaded)
				&& (!i.errorMessage || i.errorMessage.length === 0));
		}

		/** Check to close session */

		private waitForAllDownloadsAsync(delay?: number): JQueryPromise<core.IDownloadQueueItem[]> {
			let del = 200;
			if (delay) {
				const check = delay * 1.10;
				del = Math.round(check);
				if (delay > 2000)
					del = 2000;
			}

			return this.checkDownloadStatusAsync()
				.then(files => {
					if (files)
						return $.Deferred<core.IDownloadQueueItem[]>().resolve(files).promise();
					else {
						return utils.waitAsync(del).then(() => {
							return this.waitForAllDownloadsAsync(del);
						});
					}
				});
		}
	}
}
